import { useEffect, useState } from 'react'
import type { MouseEvent } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import Nav from '../components/Nav'
import { api, ApiError } from '../api/client'
import { useAuthStore } from '../store/auth'
import type { SceneMeta, Project } from '../api/types'

export default function Gallery() {
  const { t } = useTranslation()
  const nav = useNavigate()
  const user = useAuthStore((s) => s.user)
  const [scenes, setScenes] = useState<SceneMeta[]>([])
  const [projects, setProjects] = useState<Project[]>([])
  const [loading, setLoading] = useState(true)
  const [projLoading, setProjLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let alive = true
    setLoading(true)
    api
      .listScenes()
      .then((r) => {
        if (alive) {
          setScenes(r.scenes)
          setError(null)
        }
      })
      .catch((e: unknown) => {
        if (alive) setError(e instanceof ApiError ? e.message : 'Failed to load scenes')
      })
      .finally(() => {
        if (alive) setLoading(false)
      })
    return () => {
      alive = false
    }
  }, [])

  // 登录后加载我的项目
  useEffect(() => {
    if (!user) {
      setProjects([])
      return
    }
    let alive = true
    setProjLoading(true)
    api
      .listProjects()
      .then((r) => {
        if (alive) setProjects(r.projects)
      })
      .catch(() => {
        /* 静默：项目加载失败不阻塞场景浏览 */
      })
      .finally(() => {
        if (alive) setProjLoading(false)
      })
    return () => {
      alive = false
    }
  }, [user])

  const openProject = (p: Project) => nav(`/p/${p.id}`)

  const onDelete = async (e: MouseEvent, id: string) => {
    e.preventDefault()
    e.stopPropagation()
    if (!window.confirm('删除该项目？')) return
    try {
      await api.deleteProject(id)
      setProjects((ps) => ps.filter((p) => p.id !== id))
    } catch (err) {
      window.alert(err instanceof ApiError ? err.message : '删除失败')
    }
  }

  return (
    <div className="app-shell">
      <Nav />
      <div className="app-body gallery-scroll">
        <main className="gallery">
          <section className="gallery-hero">
            <h1>{t('gallery.heroTitle')}</h1>
            <p>{t('gallery.heroSubtitle')}</p>
          </section>

          {user && (
            <section className="my-projects">
              <h2 className="section-title">
                {t('gallery.myProjects')}
                {projLoading && <span className="spinner" style={{ marginLeft: 8 }} />}
              </h2>
              {projects.length === 0 ? (
                <div className="empty-hint">{t('gallery.noProjects')}</div>
              ) : (
                <div className="scene-grid">
                  {projects.map((p) => (
                    <div key={p.id} className="scene-card project-card" onClick={() => openProject(p)}>
                      <div className="sc-icon">📁</div>
                      <div className="sc-body">
                        <div className="sc-title">{p.title || p.scene_type}</div>
                        <div className="sc-desc">
                          {p.scene_type} · {new Date(p.updated_at).toLocaleDateString()}
                        </div>
                      </div>
                      <button
                        type="button"
                        className="project-del"
                        onClick={(e) => onDelete(e, p.id)}
                        title="删除"
                        aria-label="删除"
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </section>
          )}

          <h2 className="section-title">{t('gallery.scenesTitle')}</h2>

          {loading && (
            <div className="gallery-loading">
              <span className="spinner" />
            </div>
          )}
          {error && <div className="error-box">{error}</div>}

          <section className="scene-grid">
            {scenes.map((s) => (
              <Link key={s.id} to={`/s/${s.id}`} className="scene-card">
                <div className="sc-icon">{s.icon}</div>
                <div className="sc-body">
                  <div className="sc-title">{s.name}</div>
                  <div className="sc-desc">{s.description}</div>
                </div>
                {s.badge && <span className="sc-badge">{s.badge}</span>}
              </Link>
            ))}
          </section>
        </main>
      </div>
    </div>
  )
}
